// diffUtils.ts
import isEqual from 'lodash.isequal'

export type RecipientLite = {
  id?: string
  contacts_uuid?: string
  user_uuid?: string
  panda_contact_id?: string
  email?: string
  name?: string
  first_name?: string
  last_name?: string
}

// Keep enough for label rendering downstream if needed
export const liteRecipient = (r: any): RecipientLite => {
  const first = r?.first_name ?? r?.firstName ?? ''
  const last = r?.last_name ?? r?.lastName ?? ''
  const name = (r?.name ?? `${first} ${last}`).trim() || undefined

  return {
    id: r?.id ?? r?.user_uuid ?? r?.contacts_uuid ?? r?.panda_contact_id ?? r?.contact_id ?? '',
    contacts_uuid: r?.contacts_uuid,
    user_uuid: r?.user_uuid,
    panda_contact_id: r?.panda_contact_id ?? r?.contact_id,
    email: r?.email,
    first_name: first || undefined,
    last_name: last || undefined,
    name,
  }
}

export const normalizeRecipients = (recipients: any) => ({
  authorizedRecipient: Array.isArray(recipients?.authorizedRecipient)
    ? recipients.authorizedRecipient.map(liteRecipient)
    : [],
  contactRecipient: Array.isArray(recipients?.contactRecipient) ? recipients.contactRecipient.map(liteRecipient) : [],
})

export const sanitizeForDynamo = (val: any): any => {
  if (Array.isArray(val)) return val.map(sanitizeForDynamo).filter(v => v !== undefined)
  if (val && typeof val === 'object') {
    const out: any = {}
    for (const [k, v] of Object.entries(val)) {
      const sv = sanitizeForDynamo(v)
      if (sv !== undefined) out[k] = sv
    }
    return out
  }
  if (typeof val === 'function' || val === undefined) return undefined
  if (val instanceof Date) return val.toISOString()
  return val
}

const toIso = (v: any) => (v ? new Date(v).toISOString() : v)
const buildNormalizedValue = (fieldName: string, value: any) => {
  if (fieldName === 'numberOfPayments') {
    return String(value ?? '') // Ensure it's a string
  }
  return value
}
// used only for building the payload we send to Dynamo
export const buildNewValue = (fieldName: string, value: any) => {
  const v =
    fieldName === 'recipients'
      ? normalizeRecipients(value)
      : fieldName === 'numberOfPayments'
      ? buildNormalizedValue('numberOfPayments', value)
      : fieldName === 'implementationFee'
      ? (() => {
          const n = value === '' || value == null ? 0 : Number(value)
          const safe = Number.isFinite(n) ? n : 0
          // choose one:
          // return String(safe);        // store as integer/float string (no forced decimals)
          return safe.toFixed(2)
        })()
      : fieldName === 'timeCompleted'
      ? value
        ? new Date(value).toISOString()
        : null // ✅ NEW
      : value

  return sanitizeForDynamo(v)
}

// used for field-by-field diff (compact structure)
export const pickForDiff = (deal: any) => {
  if (!deal) return deal
  return {
    type: deal.type ?? '',
    numberOfPayments: deal.numberOfPayments != null ? String(deal.numberOfPayments) : '',
    startDate: toIso(deal.startDate),
    endDate: toIso(deal.endDate),
    dependenciesDueDate: toIso(deal.dependenciesDueDate),
    targetLaunchDate: toIso(deal.targetLaunchDate),
    recipients: normalizeRecipients(deal.recipients),
    implementationFee: String(deal?.implementationFee ?? '0'),
    noSignature: !!deal.noSignature,
    // Cascade fields that follow a noSignature toggle (and a few other
    // status-changing events). Previously commented out, which meant the
    // onChange('noSignature') handler in ViewAgreement set status / stage /
    // timeCompleted on the draft but the diff dropped them — only the
    // boolean made it to the server, leaving the deal in a half-converted
    // state. Including them here means they propagate when (and only when)
    // they actually change.
    status: deal.status ?? '',
    stage: deal.stage ?? '',
    dealStatus: deal.dealStatus ?? '',
    needsApproval: !!deal.needsApproval,
    timeCompleted: deal.timeCompleted ? toIso(deal.timeCompleted) : '',
    dba: deal.dba ?? '',
  }
}

export const deepEqual = (a: any, b: any) => isEqual(a, b)
